import React, { useState } from 'react';
import { submitNodeFeedbackBulk } from 'features/evals3/api/evals3Api';
import { boolWords } from './StepAnswer';

/**
 * "Label a step…" for the ticked results (datasets phase 6, PRD §4.5 bulk):
 * one step, one answer, one reason, applied to every ticked verdict that ran
 * that step. The answer is typed to the step's declared output, the same as
 * the per-step control: a choice offers its options, a score a value, a
 * yes/no step Yes / No or "the other way" (each run's own answer flipped, on
 * the server, which knows what each step said). Free-text judges are labelled
 * one at a time. The server checks each item against the version that graded
 * it and lists what it skipped.
 */
export default function BulkStepLabel({ teamId, graph, runIds, onDone }) {
  const [open, setOpen] = useState(false);
  const [nodeId, setNodeId] = useState('');
  const [draft, setDraft] = useState({});
  const [flip, setFlip] = useState(true);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const nodes = Object.entries(graph?.nodes || {})
    .filter(([, n]) => n && n.output && typeof n.output === 'object' && Object.keys(n.output).length);
  if (!teamId || !runIds.length || !nodes.length) return null;

  const node = graph.nodes[nodeId] || null;
  const fields = node ? Object.entries(node.output) : [];
  const allBool = fields.length > 0 && fields.every(([, f]) => f.type === 'boolean');
  const useFlip = allBool && flip;
  const ready = node && (useFlip || fields.every(([k]) => draft[k] !== undefined && draft[k] !== ''));

  const btn = 'rounded border px-2 py-0.5 text-[11px]';
  const on = 'border-[color:var(--border-strong)] bg-surface-hover font-semibold text-[color:var(--text-primary)]';
  const off = 'border-[color:var(--border-default)] text-[color:var(--text-secondary)] hover:bg-surface-hover';

  const save = async () => {
    if (!ready) return;
    setBusy(true); setResult(null);
    try {
      const items = runIds.map((id) => ({
        eval_run_id: id, node_id: nodeId, reason: reason.trim() || null,
        ...(useFlip ? { flip: true } : { should_have_been: Object.fromEntries(fields.map(([k]) => [k, draft[k]])) }),
      }));
      const out = await submitNodeFeedbackBulk(teamId, items);
      setResult(out);
      onDone?.(out.labelled || []);
      if (!(out.skipped || []).length) setOpen(false);
    } catch (e) {
      setResult({ error: e?.response?.data?.message || e?.response?.data?.error || e.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="relative">
      <button type="button" onClick={() => { setOpen((o) => !o); setResult(null); }} aria-expanded={open}
        className="h-8 rounded-md border border-[color:var(--border-default)] px-2.5 text-[12px] text-[color:var(--text-primary)] hover:bg-surface-hover">
        Label a step…
      </button>
      {open && (
        <div role="group" aria-label="Label a step on the selected results"
          className="absolute bottom-full right-0 z-30 mb-2 w-80 space-y-2 rounded-md border border-[color:var(--border-default)] bg-surface p-2.5 text-[11px] shadow-lg">
          <div className="font-medium text-[color:var(--text-primary)]">Should have been, on all {runIds.length}</div>
          <select value={nodeId} onChange={(e) => { setNodeId(e.target.value); setDraft({}); setFlip(true); }} aria-label="Step"
            className="w-full rounded border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-1 text-[11px] text-[color:var(--text-primary)]">
            <option value="">Choose a step…</option>
            {nodes.map(([id, n]) => <option key={id} value={id}>{n.name || id}</option>)}
          </select>
          {allBool ? (
            <div className="flex gap-1">
              <button type="button" onClick={() => setFlip(true)} className={`${btn} flex-1 ${flip ? on : off}`}>The other way</button>
              <button type="button" onClick={() => setFlip(false)} className={`${btn} flex-1 ${!flip ? on : off}`}>Set a value</button>
            </div>
          ) : null}
          {node && !useFlip ? fields.map(([k, f]) => (
            <div key={k} className="space-y-1">
              {fields.length > 1 ? <div className="text-[color:var(--text-muted)]">{k}</div> : null}
              {f.type === 'boolean' ? (
                <div className="flex gap-1">
                  {[[boolWords(k)[0], true], [boolWords(k)[1], false]].map(([label, v]) => (
                    <button key={label} type="button" onClick={() => setDraft((d) => ({ ...d, [k]: v }))} className={`${btn} flex-1 ${draft[k] === v ? on : off}`}>{label}</button>
                  ))}
                </div>
              ) : f.type === 'enum' ? (
                <div className="flex flex-wrap gap-1">
                  {(f.values || []).map((opt) => (
                    <button key={opt} type="button" onClick={() => setDraft((d) => ({ ...d, [k]: opt }))} className={`${btn} ${draft[k] === opt ? on : off}`}>{opt}</button>
                  ))}
                </div>
              ) : (
                <input type="number" aria-label={k} value={draft[k] ?? ''} min={f.min} max={f.max} step="any"
                  onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value === '' ? '' : Number(e.target.value) }))}
                  className="w-full rounded border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-1 text-[11px] text-[color:var(--text-primary)] outline-none" />
              )}
            </div>
          )) : null}
          {node ? (
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={4000} aria-label="Why"
              placeholder="Why? One reason for all"
              className="w-full rounded border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-1.5 text-[11px] text-[color:var(--text-primary)] outline-none" />
          ) : null}
          {result?.error ? <div className="text-[color:var(--feedback-error)]">{result.error}</div> : null}
          {result && !result.error ? (
            <div className="text-[color:var(--text-muted)]" role="status">
              Labelled {(result.labelled || []).length}
              {(result.skipped || []).length ? ` · ${result.skipped.length} skipped (${[...new Set(result.skipped.map((x) => x.message || x.error))].slice(0, 2).join('; ')})` : ''}
            </div>
          ) : null}
          <div className="flex items-center gap-1">
            <button type="button" disabled={!ready || busy} onClick={save}
              className="rounded bg-brand px-2 py-1 font-medium text-ink-onBrand hover:bg-brand-hover disabled:opacity-40">
              {busy ? 'Saving…' : `Label ${runIds.length}`}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="rounded px-2 py-1 text-[color:var(--text-muted)] hover:bg-surface-hover">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
